import 'react';
import {
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import {A13_FOOTER, A13_ROWS, A13Row, A_PAGE_TITLES, addDaysToDateStr, buildAssemblyDateFromDeadline} from '@/pages/components/audit-program/a-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';
import {indentPaddingLeft, resolveDisplayLevel} from '@/pages/components/audit-program/templates/rowLevel';

export interface A13ChecklistViewProps {
    header: AuditProgramSectionVO;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
    title?: string;
    rows?: A13Row[];
    footer?: typeof A13_FOOTER;
}

const cellBorder = {borderColor: 'divider'};
const headSx = {
    ...cellBorder,
    fontWeight: 600,
    fontSize: '0.75rem',
    py: 0.75,
    px: 0.5,
};
const bodySx = {
    ...cellBorder,
    py: 0.5,
    px: 0.5,
    verticalAlign: 'top' as const,
    overflow: 'hidden',
};
const fieldSx = {minWidth: 0, width: '100%', maxWidth: '100%'};

function CompactInput({
    value,
    onChange,
    align = 'left',
}: {
    value: string;
    onChange: (v: string) => void;
    align?: 'left' | 'center' | 'right';
}) {
    return (
        <InputField
            type="text"
            fullWidth
            compact
            align={align}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            sx={fieldSx}
        />
    );
}

/**
 * Layout mirrors Excel sheet A1.3:
 * Header → centered title → checklist (Comments / WP Ref) → date lines → note
 * D23 ← signed on; D24 = D23+60; D25 = D24 − random(1..10)
 */
export default function A13ChecklistView({
    header,
    values,
    onChange,
    title,
    rows,
    footer,
}: A13ChecklistViewProps) {
    const pageTitle = title ?? A_PAGE_TITLES['A1.3'].toUpperCase();
    const pageRows = rows ?? A13_ROWS;
    const pageFooter = footer ?? A13_FOOTER;
    const rowField = (rowId: string, field: string) => values[`${rowId}.${field}`] ?? '';

    const handleSignedChange = (value: string) => {
        onChange('auditReportSignedOn', value);
        if (!value) return;
        const deadline = addDaysToDateStr(value, 60);
        if (!values.deadlineAssembly && deadline) {
            onChange('deadlineAssembly', deadline);
        }
        const effectiveDeadline = values.deadlineAssembly || deadline;
        if (!values.fileAssemblyCompleted && effectiveDeadline) {
            const assembly = buildAssemblyDateFromDeadline(effectiveDeadline);
            if (assembly) onChange('fileAssemblyCompleted', assembly);
        }
    };

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a13"
                refCode="A1.3"
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.4, mb: 1.5, mt: 0.5}}
            >
                {pageTitle}
            </Typography>

            <TableContainer sx={{overflowX: 'auto'}}>
                <Table size="small" sx={{minWidth: 800, tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <colgroup>
                        <col style={{width: 40}}/>
                        <col/>
                        <col style={{width: 200}}/>
                        <col style={{width: 100}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx} colSpan={2}/>
                            <TableCell sx={headSx} align="center">Comments</TableCell>
                            <TableCell sx={headSx} align="center">WP Ref</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pageRows.map((row) => {
                            const pl = indentPaddingLeft(resolveDisplayLevel(row));
                            return (
                            <TableRow key={row.id}>
                                <TableCell sx={bodySx}>
                                    <Typography variant="body2" fontWeight={600} sx={{pl}}>{row.num}</Typography>
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <Typography variant="body2" sx={{lineHeight: 1.4, pl}}>{row.text}</Typography>
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CompactInput
                                        value={rowField(row.id, 'comments')}
                                        onChange={(v) => onChange(`${row.id}.comments`, v)}
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CompactInput
                                        value={rowField(row.id, 'wpRef')}
                                        onChange={(v) => onChange(`${row.id}.wpRef`, v)}
                                        align="center"
                                    />
                                </TableCell>
                            </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Excel footer dates: label left, value right — stacked */}
            <Box sx={{mt: 2, maxWidth: 520}}>
                <Stack spacing={1}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="body2" sx={{minWidth: 220}}>
                            {pageFooter.signedLabel}
                        </Typography>
                        <Box sx={{flex: 1}}>
                            <CompactInput
                                value={values.auditReportSignedOn ?? ''}
                                onChange={handleSignedChange}
                            />
                        </Box>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="body2" sx={{minWidth: 220}}>
                            {pageFooter.deadlineLabel}
                        </Typography>
                        <Box sx={{flex: 1}}>
                            <CompactInput
                                value={values.deadlineAssembly ?? ''}
                                onChange={(v) => onChange('deadlineAssembly', v)}
                            />
                        </Box>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="body2" sx={{minWidth: 220}}>
                            {pageFooter.assemblyCompletedLabel}
                        </Typography>
                        <Box sx={{flex: 1}}>
                            <CompactInput
                                value={values.fileAssemblyCompleted ?? ''}
                                onChange={(v) => onChange('fileAssemblyCompleted', v)}
                            />
                        </Box>
                    </Stack>
                </Stack>
            </Box>

            <Typography
                variant="caption"
                color="text.secondary"
                sx={{display: 'block', mt: 2, whiteSpace: 'pre-line', lineHeight: 1.5}}
            >
                {pageFooter.footerNote}
            </Typography>
        </Box>
    );
}
